import type { Member, ViewAsLogEntry } from '@epc/contracts';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import { useEffect } from 'react';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { meQuery, useMe } from '../lib/session';
import { Button } from '../ui/button';
import { EmptyState, ErrorBox, Spinner, toast } from '../ui/feedback';
import { Panel } from '../ui/layout';

/**
 * «Посмотреть глазами пользователя» (ДС-08): кнопка у сотрудника, полоса режима вверху страницы и журнал
 * включений. В режиме портал показывает меню, разделы и данные по правам выбранного человека; изменения
 * отключены (сервер отвечает 403 view_as), деньги — не выше уровня того, кто смотрит.
 */

/** После входа в режим и выхода из него: /me и все данные перечитываются уже с другими правами */
async function reloadAll(qc: QueryClient) {
  await qc.fetchQuery({ ...meQuery, staleTime: 0 });
  void qc.resetQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
}

const REASONS: Record<NonNullable<ViewAsLogEntry['endReason']>, string> = {
  stop: 'вышел сам',
  replaced: 'переключился на другого',
  expired: 'время вышло',
  revoked: 'нет права или человек ушёл',
};

/** Кнопка в строке сотрудника: себя и владельца (если вы не владелец) смотреть нельзя */
export function ViewAsButton({ member }: { member: Member }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const start = useMutation({
    mutationFn: () => api('/admin/view-as', { body: { userId: member.userId } }),
    onSuccess: async () => {
      await reloadAll(qc);
      navigate({ to: '/' });
      toast(`Вы смотрите портал глазами: ${member.fullName}`);
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось включить режим', 'danger'),
  });
  if (member.userId === me.user.id || member.status !== 'active') return null;
  if (member.isOwner && !me.tenant?.isOwner) return null;
  return (
    <Button
      size="sm"
      variant="ghost"
      icon={<Eye className="size-4" />}
      loading={start.isPending}
      onClick={() => start.mutate()}
      title="Посмотреть портал так, как его видит этот человек (только просмотр)"
    >
      Глазами
    </Button>
  );
}

/** Полоса режима: кого смотрим, до какого времени и «Выйти» — на каждой странице, поверх шапки */
export function ViewAsBanner() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const stop = useMutation({
    mutationFn: () => api('/auth/view-as/stop', { method: 'POST' }),
    onSuccess: async () => {
      await reloadAll(qc);
      navigate({ to: '/admin/members' });
      toast('Вы снова работаете от своего имени');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось выйти из режима', 'danger'),
  });
  const va = me.viewAs;
  const until = va?.until;
  // В назначенное время режим заканчивается сам: страница перечитывает права и убирает полосу
  useEffect(() => {
    if (!until) return;
    const ms = Math.max(0, Date.parse(until) - Date.now()) + 1000;
    if (ms > 2_147_000_000) return;
    const t = setTimeout(() => void reloadAll(qc), ms);
    return () => clearTimeout(t);
  }, [until, qc]);
  if (!va) return null;
  return (
    <div className="border-b border-warning/40 bg-warning-soft text-ink print:hidden" role="status">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 md:px-8">
        <Eye className="size-5 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0 flex-1 basis-[220px] text-[13px] leading-5">
          <span className="font-semibold">Вы смотрите глазами: {va.fullName}.</span>{' '}
          <span className="text-ink-2">
            Только просмотр — действия отключены. Режим закончится сам в {dateTime(va.until)}.
          </span>
        </div>
        <Button size="sm" variant="secondary" loading={stop.isPending} onClick={() => stop.mutate()}>
          Выйти из режима
        </Button>
      </div>
    </div>
  );
}

/** Журнал: кто, кого и когда смотрел — для администратора компании */
export function ViewAsLogPanel() {
  const q = useQuery({
    queryKey: ['admin', 'view-as', 'log'],
    queryFn: () => api<ViewAsLogEntry[]>('/admin/view-as/log'),
  });
  return (
    <Panel
      title="Просмотр глазами пользователя"
      meta={q.data?.length ? `включений: ${q.data.length}` : undefined}
      bodyClassName="p-0"
    >
      {q.isPending ? (
        <div className="px-5">
          <Spinner />
        </div>
      ) : q.isError ? (
        <div className="p-5">
          <ErrorBox error={q.error} />
        </div>
      ) : q.data.length ? (
        <ul>
          {q.data.map((r) => (
            <li key={r.id} className="border-t border-line px-5 py-3 text-[14px] first:border-0">
              <div className="font-medium text-ink">
                {r.viewerName} → {r.targetName}
              </div>
              <div className="text-[13px] text-ink-2">
                {dateTime(r.startedAt)}
                {r.endedAt ? ` — ${dateTime(r.endedAt)}` : ' — идёт сейчас'}
                {r.endReason ? `, ${REASONS[r.endReason]}` : ''}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="p-5">
          <EmptyState title="Ещё никто не смотрел">
            Кнопка «Глазами» у сотрудника показывает портал так, как его видит этот человек: меню, разделы и
            данные по его правам. Изменить в этом режиме ничего нельзя.
          </EmptyState>
        </div>
      )}
    </Panel>
  );
}
